<script setup lang="ts">
import Footer from '@/components/Footer.vue';
import Header from '@/components/Header.vue';
import { Head } from '@inertiajs/vue3';
import { Building, Coins, CreditCard, Megaphone, Smartphone, Users } from 'lucide-vue-next';

interface Product {
    icon: any;
    title: string;
    description: string;
}

interface BusinessModelItem {
    icon: any;
    title: string;
    description: string;
}

const products: Product[] = [
    {
        icon: Smartphone,
        title: 'Digital Safe',
        description:
            'An app for securing cash for individuals, companies and public sectors, designed to secure savings and taxes. Our system has no competition, the system is innovative, with a patent registered in the Patent Office. Our app is also an app promoting the green transformation.',
    },
    {
        icon: CreditCard,
        title: 'Digital Gold',
        description:
            'A service similar to Blik payments. In a nutshell, the system is a new electronic payment platform that uses banknotes for cashless transactions.',
    },
];

const businessModel: BusinessModelItem[] = [
    {
        icon: Megaphone,
        title: 'Internet Activities',
        description: 'Advertisements, subscriptions giving additional options in services, Marketing and PR',
    },
    {
        icon: Coins,
        title: 'Micro-transactions',
        description: 'To make the brand recognizable in the shortest possible time, we will use website creators and influencer marketing',
    },
    {
        icon: Users,
        title: 'Token-based Financing',
        description: 'The main source of financing will be tokens, initially distributed for promotion, then sold at later stages',
    },
];
</script>

<template>
    <Head title="Who We Are">
        <meta
            head-key="description"
            name="description"
            content="Our company is the parent company that will be responsible for matching subsidiaries. We want to start our business with our two innovative products."
        />
        <meta
            name="keywords"
            content="Digital Safe, Who We Are, Digital Safe Who We Are, Company Overview, Our Products, Business Model, Funding & Future Plans"
        />
    </Head>
    <div class="min-h-screen bg-background">
        <Header />
        <main class="pt-20">
            <!-- Hero Section -->
            <section class="relative overflow-hidden py-20">
                <div class="bg-gradient-subtle absolute inset-0 opacity-50"></div>
                <div class="relative z-10 container mx-auto max-w-4xl px-4 text-center">
                    <h1 class="mb-6 text-4xl font-bold md:text-6xl">
                        Who <span class="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">We Are</span>
                    </h1>
                    <p class="text-lg leading-relaxed text-muted-foreground md:text-xl">
                        Our company is the parent company that will be responsible for matching subsidiaries. We want to start our business with our
                        two innovative products.
                    </p>
                </div>
            </section>

            <!-- Company Overview -->
            <section class="py-20">
                <div class="container mx-auto max-w-4xl px-4">
                    <div class="mb-8 flex items-center space-x-4">
                        <div class="glow flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                            <Building class="h-6 w-6 text-primary-foreground" />
                        </div>
                        <h2 class="text-3xl font-bold md:text-4xl">Company Overview</h2>
                    </div>

                    <div class="glass-card mb-8 p-8">
                        <p class="mb-6 text-lg leading-relaxed">
                            Currently, our work is entirely financed from our own resources. We have up to 12 months until the products/services are
                            completed so that they can enter the market in an early version, with assumed financing.
                        </p>

                        <div class="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
                            <div class="text-center">
                                <div
                                    class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/60"
                                >
                                    <Building class="h-8 w-8 text-primary-foreground" />
                                </div>
                                <h3 class="font-semibold">Self-Financed</h3>
                                <p class="mt-2 text-sm text-muted-foreground">Currently funded from our own resources</p>
                            </div>

                            <div class="text-center">
                                <div
                                    class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-accent to-accent/60"
                                >
                                    <span class="text-xl font-bold text-accent-foreground">12</span>
                                </div>
                                <h3 class="font-semibold">Months Timeline</h3>
                                <p class="mt-2 text-sm text-muted-foreground">Until market-ready early version</p>
                            </div>

                            <div class="text-center">
                                <div
                                    class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-secondary to-secondary/60"
                                >
                                    <Users class="h-8 w-8 text-secondary-foreground" />
                                </div>
                                <h3 class="font-semibold">Growing Team</h3>
                                <p class="mt-2 text-sm text-muted-foreground">Building our specialist team</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Products Section -->
            <section class="bg-gradient-subtle py-20">
                <div class="container mx-auto max-w-4xl px-4">
                    <h2 class="mb-12 text-center text-3xl font-bold md:text-4xl">Our <span class="text-primary">Products</span></h2>

                    <div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
                        <div v-for="(product, index) in products" :key="index" class="glass-card hover:glow p-8 transition-all duration-300">
                            <div class="flex items-start space-x-4">
                                <div class="glow flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-hero">
                                    <component :is="product.icon" class="h-8 w-8 text-primary-foreground" />
                                </div>
                                <div>
                                    <h3 class="mb-4 text-2xl font-semibold text-foreground">{{ product.title }}</h3>
                                    <p class="leading-relaxed text-muted-foreground">{{ product.description }}</p>
                                    <div v-if="index === 0" class="mt-4 flex flex-wrap gap-2">
                                        <span class="rounded-full bg-primary/20 px-3 py-1 text-sm text-primary">Patent Registered</span>
                                        <span class="rounded-full bg-accent/20 px-3 py-1 text-sm text-accent">Green Transformation</span>
                                        <span class="rounded-full bg-secondary/20 px-3 py-1 text-sm text-secondary">No Competition</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mt-12 text-center">
                        <div class="glass-card inline-block p-6">
                            <p class="text-muted-foreground">
                                <span class="font-semibold text-accent">Note:</span> We do not want to reveal details due to competition
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Business Model Section -->
            <section class="py-20">
                <div class="container mx-auto max-w-4xl px-4">
                    <h2 class="mb-12 text-center text-3xl font-bold md:text-4xl">Business <span class="text-primary">Model</span></h2>

                    <div class="mb-12 grid grid-cols-1 gap-8 md:grid-cols-3">
                        <div
                            v-for="(item, index) in businessModel"
                            :key="index"
                            class="glass-card hover:glow glow-hover p-6 text-center transition-all duration-300"
                        >
                            <div class="glow mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-hero">
                                <component :is="item.icon" class="h-8 w-8 text-primary-foreground" />
                            </div>
                            <h3 class="mb-3 text-xl font-semibold">{{ item.title }}</h3>
                            <p class="text-sm leading-relaxed text-muted-foreground">{{ item.description }}</p>
                        </div>
                    </div>

                    <div class="glass-card p-8">
                        <h3 class="mb-6 text-center text-2xl font-semibold">Funding & Future Plans</h3>
                        <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
                            <div>
                                <h4 class="mb-3 font-semibold text-primary">Looking for Funds</h4>
                                <ul class="space-y-2 text-muted-foreground">
                                    <li>• Computer equipment meeting hardware requirements</li>
                                    <li>• Employ specialists for areas we cannot cover</li>
                                    <li>• Marketing and PR infrastructure</li>
                                </ul>
                            </div>
                            <div>
                                <h4 class="mb-3 font-semibold text-primary">Token Strategy</h4>
                                <ul class="space-y-2 text-muted-foreground">
                                    <li>• Initially distribute tokens for product promotion</li>
                                    <li>• Later stage token sales for funding</li>
                                    <li>• Future: tokens required for cash security apps</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Contact Section -->
            <section class="bg-gradient-subtle py-20">
                <div class="container mx-auto max-w-2xl px-4 text-center">
                    <h2 class="mb-8 text-3xl font-bold md:text-4xl">Interested in Our <span class="text-primary">Project?</span></h2>
                    <p class="mb-8 text-lg leading-relaxed text-muted-foreground">
                        The rest of our activities depend on the resources we will have at our disposal. We hope you are interested - more information
                        is available privately.
                    </p>

                    <div class="glass-card p-8">
                        <p class="mb-4 text-muted-foreground">Get in touch for more details</p>
                        <a
                            href="mailto:peter@digitalsafe.io"
                            class="hover:glow inline-flex items-center space-x-2 rounded-lg bg-gradient-hero px-6 py-3 font-semibold text-primary-foreground transition-all duration-300"
                        >
                            <span>peter@digitalsafe.io</span>
                        </a>
                    </div>
                </div>
            </section>
        </main>
        <Footer />
    </div>
</template>
